import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { PackageKeys } from '@conversed/shared/src/central/types/package.type';
import { PlatformFeatureType } from '@conversed/shared/src/central/types/platform-features.type';
import {
  ProvisioningEnvSchema,
  ProvisioningStatusSchema,
  ProvisioningStepStatusSchema,
} from '@conversed/shared/src/central/types/provisioning.type';
import { StudioType } from '@conversed/shared/src/central/types/studio.type';
import { z } from 'zod';
import { createRESTContract } from '../../index';

const TFileSchema = z.object({
  name: z.string(),
  size: z.number(),
  type: z.string(),
  base64: z.string(),
});

const CreateCompanyMemberSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  profilePic: z.union([TFileSchema, z.null()]),
  roles: z.array(z.string()),
  inboxes: z.array(z.string()),
});

export const CreateCompanySchema = z.object({
  company: z
    .object({
      name: z.string(),
      icon: z.union([TFileSchema, z.null()]),
      vat: z.string().optional(),
      defaultLanguage: z.string(),
      country: z.string(),
      websiteUrl: z.string().optional(),
      studioType: z.enum(StudioType),
      parentCompanyId: z.string().optional(),
      // * Core instance URL of a hand-built deployment. Required when provision
      // * is off - the proxy mapping points at it.
      botBackendUrl: z.string().optional(),
      // * S3 bucket of a hand-built deployment. Required when provision is off.
      bucket: z.string().optional(),
      // * Opt-in toggle (default off): when true the create endpoint runs the AWS
      // * provisioning pipeline; when false the admin has already built the
      // * instance by hand and supplies its botBackendUrl and bucket, so the
      // * company is created ready to use. The two routes are mutually exclusive.
      provision: z.boolean().default(false),
    })
    .refine(
      (data) => {
        if (!data.provision && !data.botBackendUrl) {
          return false;
        }

        return true;
      },
      {
        message: 'Backend URL is required when the company is not provisioned',
        path: ['botBackendUrl'],
      },
    )
    .refine(
      (data) => {
        if (!data.provision && !data.bucket) {
          return false;
        }

        return true;
      },
      {
        message: 'Bucket is required when the company is not provisioned',
        path: ['bucket'],
      },
    ),
  member: CreateCompanyMemberSchema.optional(),
});

export const BulkInviteUsersSchema = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.email(),
    roles: z.string().array(),
  })
  .array();

export const CompanyMembersSchema = z
  .object({
    id: z.string(),
    profilePic: z.string().nullable(),
    email: z.string(),
    roles: z.array(z.string()),
    grantedBy: z.string(),
    mfaEnabled: z.boolean(),
  })
  .array();

const PackageSchema = z
  .object({
    isActive: z.boolean(),
    startDate: z.string().nullable().optional(),
    endDate: z.string().nullable().optional(),
  })
  .refine(
    (data) => {
      if (data.isActive && !data.startDate) {
        return false;
      }
      return true;
    },
    {
      message: 'Start date is required when package is active',
      path: ['startDate'],
    },
  )
  .refine(
    (data) => {
      if (data.isActive && !data.endDate) {
        return false;
      }
      return true;
    },
    {
      message: 'End date is required when package is active',
      path: ['endDate'],
    },
  );

export const CompanyPackagesSchema = z.object({
  [PackageKeys.JUNIOR]: PackageSchema.optional(),
  [PackageKeys.MEDIOR]: PackageSchema.optional(),
  [PackageKeys.SENIOR]: PackageSchema.optional(),
});

export const CompanyPlatformFeaturesSchema = z.array(
  z.enum(PlatformFeatureType),
);

const ProvisionStepSchema = z.object({
  step: z.number(),
  name: z.string().optional(),
  status: ProvisioningStepStatusSchema,
  resourceId: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});

// * Public step shape - drops the internal AWS resourceId (mirrors
// * TProvisionStepPublic, which also omits the operator-facing skipReason this
// * schema never carried).
const ProvisionStepPublicSchema = ProvisionStepSchema.omit({
  resourceId: true,
});

// * Sanitized provision read model for the superAdmin status page: no
// * credentials, no pendingIcon, no per-step resourceId. Nullable for legacy
// * companies that were never provisioned.
export const ProvisionStatusResponseSchema = z
  .object({
    status: ProvisioningStatusSchema,
    identifier: z.string(),
    slug: z.string(),
    env: ProvisioningEnvSchema,
    startedAt: z.date(),
    completedAt: z.date().nullable(),
    failedAt: z.date().nullable(),
    deprovisionedAt: z.date().nullable(),
    steps: z.array(ProvisionStepPublicSchema),
  })
  .nullable();

export type ProvisionStatusResponseDTO = z.infer<
  typeof ProvisionStatusResponseSchema
>;

// * Returns the new company's id so the frontend can route straight to the
// * provisioning status page after a provision:true create.
export const CreateCompanyResponseSchema = z.object({
  companyId: z.string(),
});

export type CreateCompanyResponseDTO = z.infer<
  typeof CreateCompanyResponseSchema
>;

export type CreateCompanyDTO = z.infer<typeof CreateCompanySchema>;
export type CreateCompanyMemberDTO = z.infer<typeof CreateCompanyMemberSchema>;
export type BulkInviteUsersDTO = z.infer<typeof BulkInviteUsersSchema>;

export const companyContract = createRESTContract({
  createCompany: {
    method: 'POST',
    path: '/companies',
    permissions: [PERMISSIONS.COMPANY.COMPANY_CREATE],
    body: CreateCompanySchema,
    response: CreateCompanyResponseSchema,
  },
  retryProvisioning: {
    method: 'POST',
    path: '/companies/:companyId/provision/retry',
    permissions: [PERMISSIONS.COMPANY.COMPANY_CREATE],
    body: z.void(),
    response: z.void(),
  },
  getProvisionStatus: {
    method: 'GET',
    path: '/companies/:companyId/provision',
    permissions: [PERMISSIONS.COMPANY.COMPANY_CREATE],
    response: ProvisionStatusResponseSchema,
  },
  deprovision: {
    method: 'POST',
    path: '/companies/:companyId/provision/deprovision',
    permissions: [PERMISSIONS.COMPANY.COMPANY_CREATE],
    body: z.void(),
    response: ProvisionStatusResponseSchema,
  },
  bulkInviteUsers: {
    method: 'POST',
    path: '/bulk-invite',
    permissions: [PERMISSIONS.COMPANY.COMPANY_MEMBERS_BULK_INVITE],
    body: BulkInviteUsersSchema,
    response: CompanyMembersSchema,
  },
});
